// @cogo/template — deterministic slot auto-proposal scanner
//
// PURE leaf module: no LLM, no I/O, no Date.now/Math.random. Given an
// AppBundle, walks its files structurally and proposes SlotDecl candidates
// for every content position that looks safe to templatize (element text/
// media fields, top-level app.json strings, datasource endpoints, and
// top-level record-list arrays). Never proposes expression-bound values or
// id-less element positions (behavior, not content — not templatizable).
//
// Determinism: output order = file path ascending, then tree preorder
// within a block (and, within app.json, its own key order). slotId
// collisions are deduped with "-2", "-3", ... suffixes in emission order.

import type { AppBundle, Json, SlotAddress, SlotConstraint, SlotDecl, SlotType } from './types'

const ELEMENT_FIELDS = ['text', 'src', 'alt', 'placeholder', 'label'] as const
type ElementField = (typeof ELEMENT_FIELDS)[number]

const UI_JSON_RE = /^blocks\/([^/]+)\/ui\.json$/
const DATA_JSON_RE = /^blocks\/([^/]+)\/data\.json$/

function isPlainObject(value: unknown): value is { [k: string]: Json } {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

/** kebab-case a joined identifier string (camelCase boundaries + any non-alnum run -> single hyphen). */
function kebabCase(input: string): string {
  return input
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '')
}

/** RFC 6901 JSON Pointer token escaping (mirrors canonical.ts's unescape, in reverse). */
function pointerToken(key: string): string {
  return key.replace(/~/g, '~0').replace(/\//g, '~1')
}

/** Dash-joined form of a JSON pointer's own (already-escaped) segments, e.g. "/a/0/b" -> "a-0-b". */
function pointerDashes(pointer: string): string {
  return pointer.slice(1).replace(/\//g, '-')
}

function containsExpressionBinding(value: string): boolean {
  return value.includes('#{')
}

interface Candidate {
  baseSlotId: string
  type: SlotType
  address: SlotAddress
  required: boolean
  constraint?: SlotConstraint
  sourceValue: Json
}

// ---------------------------------------------------------------------------
// element candidates — recursive preorder walk of a block's ui.json tree.
// Every plain-object node with a non-empty string `id` is a candidate; its
// text/src/alt/placeholder/label string fields (checked in that order) each
// become a slot, unless the value is an expression binding ("#{...}").
// ---------------------------------------------------------------------------

/** Counts `id` occurrences across every plain-object node in a block's ui.json tree. */
function countElementIds(tree: Json): Map<string, number> {
  const counts = new Map<string, number>()

  function walk(node: Json): void {
    if (Array.isArray(node)) {
      for (const item of node) walk(item)
      return
    }
    if (!isPlainObject(node)) return
    const id = node.id
    if (typeof id === 'string' && id.length > 0) {
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }
    for (const key of Object.keys(node)) {
      walk(node[key])
    }
  }

  walk(tree)
  return counts
}

function elementCandidates(blockId: string, tree: Json): Candidate[] {
  const out: Candidate[] = []
  // Invariant: the scanner emits only unique (blockId, elementId)
  // addresses — maskBundle resolves first-preorder-match only, so an id
  // that appears more than once in a block would leave one physical node
  // double-masked and its sibling(s) leaking raw content into the
  // structure hash. Uniqueness of the address is therefore the scanner's
  // responsibility, not maskBundle's: pre-scan for duplicate ids and treat
  // them the same as id-less elements (non-proposable).
  const idCounts = countElementIds(tree)

  function walk(node: Json): void {
    if (Array.isArray(node)) {
      for (const item of node) walk(item)
      return
    }
    if (!isPlainObject(node)) return
    const id = node.id
    if (typeof id === 'string' && id.length > 0 && idCounts.get(id) === 1) {
      for (const field of ELEMENT_FIELDS) {
        const value = node[field]
        if (typeof value !== 'string') continue
        if (containsExpressionBinding(value)) continue
        out.push(elementCandidate(blockId, id, field, value))
      }
    }
    for (const key of Object.keys(node)) {
      walk(node[key])
    }
  }

  walk(tree)
  return out
}

function elementCandidate(blockId: string, elementId: string, field: ElementField, value: string): Candidate {
  const isImage = field === 'src' && (value.startsWith('http') || value.startsWith('/'))
  const type: SlotType = isImage ? 'image' : 'text'
  const constraint: SlotConstraint = isImage ? { format: 'url' } : { maxLength: Math.max(64, 2 * value.length) }
  return {
    baseSlotId: kebabCase(`${blockId}-${elementId}-${field}`),
    type,
    address: { kind: 'element', blockId, elementId, field },
    required: field === 'text' || field === 'src',
    constraint,
    sourceValue: value,
  }
}

// ---------------------------------------------------------------------------
// app candidates — app.json's own key order: /name, /description,
// /navigation/<i>/label, and each `datasources` entry.
//
// `datasources` is a canonical OBJECT MAP keyed by datasourceId
// (packages/cogo-schema/v4/app.cogo.schema.json ~L126) — NOT an array of
// `{id, baseUrl}` records. A legacy array-shaped `datasources` value is
// therefore never proposed from (isPlainObject rejects arrays), by design.
//
// Each map entry can independently yield up to two disjoint candidate
// kinds, keyed on different fields of the SAME entry:
//  - datasource-endpoint: entries with a string `baseUrl` (a remote
//    datasource — the endpoint itself is templatizable content).
//  - record-list: entries with `type:"static"` and a string `path` (a
//    bundle-level data file reference — the array of records IT points to
//    is templatizable content). A static datasource has no `baseUrl` at
//    all, so these two candidate kinds never collide on the same entry in
//    practice.
// ---------------------------------------------------------------------------

function appCandidates(appJson: Json, files: AppBundle['files']): Candidate[] {
  if (!isPlainObject(appJson)) return []
  const out: Candidate[] = []
  for (const key of Object.keys(appJson)) {
    if (key === 'name' || key === 'description') {
      const value = appJson[key]
      if (typeof value === 'string' && !containsExpressionBinding(value)) {
        out.push(appPointerCandidate(`/${key}`, value))
      }
      continue
    }
    if (key === 'navigation') {
      const nav = appJson[key]
      if (Array.isArray(nav)) {
        nav.forEach((item, i) => {
          if (!isPlainObject(item)) return
          const label = item.label
          if (typeof label === 'string' && !containsExpressionBinding(label)) {
            out.push(appPointerCandidate(`/navigation/${i}/label`, label))
          }
        })
      }
      continue
    }
    if (key === 'datasources') {
      const datasources = appJson[key]
      if (!isPlainObject(datasources)) continue
      for (const datasourceId of Object.keys(datasources)) {
        const entry = datasources[datasourceId]
        if (!isPlainObject(entry)) continue
        // datasourceId (the map key) may itself be expression-bound in
        // principle — defensively excluded from both candidate kinds below,
        // same as a value being expression-bound.
        if (containsExpressionBinding(datasourceId)) continue

        const baseUrl = entry['baseUrl']
        if (typeof baseUrl === 'string' && !containsExpressionBinding(baseUrl)) {
          out.push(datasourceCandidate(datasourceId, baseUrl))
        }

        const path = entry['path']
        if (entry['type'] === 'static' && typeof path === 'string' && !containsExpressionBinding(path)) {
          const rl = staticDatasourceRecordListCandidate(datasourceId, path, files)
          if (rl !== null) out.push(rl)
        }
      }
    }
  }
  return out
}

function appPointerCandidate(pointer: string, value: string): Candidate {
  return {
    baseSlotId: kebabCase(`app-${pointerDashes(pointer)}`),
    type: 'text',
    address: { kind: 'app', pointer },
    required: false,
    // Same formula as elementCandidate's text constraint (line ~126) — an
    // app-pointer text slot (app-name, app-description, nav labels) is user
    // input at instantiation time exactly like an element text slot, and
    // was previously left with NO constraint at all (unbounded slot value
    // accepted by validate-slots.ts, e.g. an arbitrarily long quoted
    // project-name flowing into the app-name slot unvalidated).
    constraint: { maxLength: Math.max(64, 2 * value.length) },
    sourceValue: value,
  }
}

function datasourceCandidate(datasourceId: string, baseUrl: string): Candidate {
  return {
    baseSlotId: kebabCase(`ds-${datasourceId}`),
    type: 'datasource-endpoint',
    address: { kind: 'datasource', datasourceId, field: 'baseUrl' },
    required: false,
    sourceValue: baseUrl,
  }
}

// ---------------------------------------------------------------------------
// record-list candidates — arrays-of-objects addressed by a `file` + a JSON
// Pointer into it. Two producers share the same candidate builder:
//  (a) recordListCandidates: top-level arrays-of-objects in a block's own
//      `blocks/<id>/data.json` (root must be a plain object; a bare
//      top-level array there cannot be addressed by a non-empty JSON
//      pointer and is therefore never proposed from this producer).
//  (b) staticDatasourceRecordListCandidate: the file a `type:"static"`
//      datasource entry's `path` references, which — per the real
//      `data/<name>.json` convention used across this repo — is typically a
//      BARE top-level array (pointer `""`, RFC 6901 whole-document), but an
//      object with a single top-level array-of-objects property (e.g.
//      `{"categories":[...]}`) is also handled.
// ---------------------------------------------------------------------------

function buildRecordListCandidate(file: string, pointer: string, baseSlotId: string, value: { [k: string]: Json }[]): Candidate {
  const first = value[0]
  const recordSchema: { [k: string]: Json } = {}
  for (const k of Object.keys(first)) recordSchema[k] = null
  return {
    baseSlotId,
    type: 'record-list',
    address: { kind: 'record-list', file, pointer },
    required: false,
    constraint: { minCount: 1, maxCount: 2 * value.length, recordSchema },
    sourceValue: value,
  }
}

function isNonEmptyArrayOfObjects(value: unknown): value is { [k: string]: Json }[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPlainObject)
}

function recordListCandidates(blockId: string, dataJson: Json): Candidate[] {
  if (!isPlainObject(dataJson)) return []
  const out: Candidate[] = []
  const file = `blocks/${blockId}/data.json`
  for (const key of Object.keys(dataJson)) {
    const value = dataJson[key]
    if (!isNonEmptyArrayOfObjects(value)) continue
    const pointer = `/${pointerToken(key)}`
    out.push(buildRecordListCandidate(file, pointer, kebabCase(`${blockId}-records-${pointerDashes(pointer)}`), value))
  }
  return out
}

/**
 * Resolves a `type:"static"` datasource entry's `path` against `files`
 * (stripping a leading "/", since app.json paths are commonly
 * root-absolute) and proposes a record-list candidate if the referenced
 * file's content is either a bare top-level array-of-objects, or a plain
 * object with exactly one top-level array-of-objects property. Returns
 * `null` (no candidate, never throws) when the file is absent or doesn't
 * match either shape — a dangling/unfamiliar datasource path is simply not
 * templatizable, not an error.
 */
function staticDatasourceRecordListCandidate(datasourceId: string, path: string, files: AppBundle['files']): Candidate | null {
  const filePath = path.startsWith('/') ? path.slice(1) : path
  const fileJson = files[filePath]
  if (fileJson === undefined) return null
  const baseSlotId = kebabCase(`ds-${datasourceId}-records`)

  if (isNonEmptyArrayOfObjects(fileJson)) {
    return buildRecordListCandidate(filePath, '', baseSlotId, fileJson)
  }
  if (isPlainObject(fileJson)) {
    const arrayKeys = Object.keys(fileJson).filter((k) => isNonEmptyArrayOfObjects(fileJson[k]))
    if (arrayKeys.length === 1) {
      const key = arrayKeys[0]
      return buildRecordListCandidate(filePath, `/${pointerToken(key)}`, baseSlotId, fileJson[key] as { [k: string]: Json }[])
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// entry point
// ---------------------------------------------------------------------------

export function proposeSlots(bundle: AppBundle): SlotDecl[] {
  const paths = Object.keys(bundle.files).sort()
  const candidates: Candidate[] = []
  for (const path of paths) {
    if (path === 'app.json') {
      candidates.push(...appCandidates(bundle.files[path], bundle.files))
      continue
    }
    const uiMatch = UI_JSON_RE.exec(path)
    if (uiMatch) {
      candidates.push(...elementCandidates(uiMatch[1], bundle.files[path]))
      continue
    }
    const dataMatch = DATA_JSON_RE.exec(path)
    if (dataMatch) {
      candidates.push(...recordListCandidates(dataMatch[1], bundle.files[path]))
    }
  }

  const seen = new Map<string, number>()
  return candidates.map((c) => {
    const count = seen.get(c.baseSlotId) ?? 0
    seen.set(c.baseSlotId, count + 1)
    const slotId = count === 0 ? c.baseSlotId : `${c.baseSlotId}-${count + 1}`
    const decl: SlotDecl = {
      slotId,
      type: c.type,
      address: c.address,
      required: c.required,
      sourceValue: c.sourceValue,
    }
    if (c.constraint) decl.constraint = c.constraint
    return decl
  })
}
